// src/features/task/TaskCustomFieldsPanel.tsx
// Custom fields on a task, driven by GetTaskCustomFields (values + definitions).
// Edit mode sets values (SetTaskCustomField) and clears them (RemoveTaskCustomField).

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useTask, useI18n } from '@react/hooks';
import {
  Card,
  CardContent,
  Badge,
} from '@react/components';
import { Select } from '@/components/Select';
import type { CustomFieldValue, CustomFieldDefinition } from '@core/types/task';
import { formatDateTime } from '@/features/task/logic';
import { errMsg } from '@/features/task/panelHelpers';

interface TaskCustomFieldsPanelProps {
  taskId: string;
  editable?: boolean;
  /** Render without the Card chrome — the host groups and frames it. */
  bare?: boolean;
}

function renderValue(value: CustomFieldValue | undefined, def: CustomFieldDefinition, t: (k: string) => string) {
  if (!value) return <span className="text-sm text-muted-foreground">--</span>;
  const ft = value.field_type || def.field_type;
  switch (ft) {
    case 'CUSTOM_FIELD_TYPE_NUMBER':
      return <span className="text-sm text-foreground font-mono">{value.number_value ?? '--'}</span>;
    case 'CUSTOM_FIELD_TYPE_DATE':
    case 'CUSTOM_FIELD_TYPE_DATETIME':
      return <span className="text-sm text-foreground">{value.date_value ? formatDateTime(value.date_value) : '--'}</span>;
    case 'CUSTOM_FIELD_TYPE_SELECT': {
      const opt = def.options?.find((o) => o.slug === value.select_value);
      return opt ? <Badge variant="secondary" size="sm">{opt.label}</Badge> : <span className="text-sm text-foreground">{value.select_value || '--'}</span>;
    }
    case 'CUSTOM_FIELD_TYPE_CHECKBOX':
      return <span className="text-sm text-foreground">{value.checkbox_value ? t('common.yes') : t('common.no')}</span>;
    case 'CUSTOM_FIELD_TYPE_URL':
      return value.url_value
        ? <a href={value.url_value} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline truncate block">{value.url_value}</a>
        : <span className="text-sm text-muted-foreground">--</span>;
    default:
      return <span className="text-sm text-foreground">{value.text_value || '--'}</span>;
  }
}

function FieldInput({ def, value, onChange }: {
  def: CustomFieldDefinition;
  value: CustomFieldValue | undefined;
  onChange: (fieldId: string, payload: any) => void;
}) {
  const fieldId = def.entity?.id || '';
  const cls = 'w-full rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground';
  switch (def.field_type) {
    case 'CUSTOM_FIELD_TYPE_NUMBER':
      return (
        <input
          type="text"
          inputMode="decimal"
          aria-label={def.display_name}
          defaultValue={value?.number_value ?? ''}
          placeholder={def.placeholder || ''}
          className={cls}
          onBlur={(e) => { const v = e.target.value.trim(); onChange(fieldId, { number_value: v === '' ? undefined : parseFloat(v) }); }}
        />
      );
    case 'CUSTOM_FIELD_TYPE_DATE':
      return <input type="date" aria-label={def.display_name} defaultValue={value?.date_value ? String(value.date_value).substring(0, 10) : ''} className={cls} onChange={(e) => onChange(fieldId, { date_value: e.target.value ? new Date(e.target.value).toISOString() : undefined })} />;
    case 'CUSTOM_FIELD_TYPE_SELECT':
      return (
        <Select value={value?.select_value || ''} onChange={(e) => onChange(fieldId, { select_value: e.target.value })}>
          <option value="">--</option>
          {(def.options || []).map((o) => <option key={o.slug} value={o.slug}>{o.label}</option>)}
        </Select>
      );
    case 'CUSTOM_FIELD_TYPE_CHECKBOX':
      return <input type="checkbox" aria-label={def.display_name} defaultChecked={value?.checkbox_value || false} className="h-4 w-4 rounded border-input" onChange={(e) => onChange(fieldId, { checkbox_value: e.target.checked })} />;
    case 'CUSTOM_FIELD_TYPE_URL':
      return <input type="url" aria-label={def.display_name} defaultValue={value?.url_value || ''} placeholder="https://..." className={cls} onBlur={(e) => onChange(fieldId, { url_value: e.target.value })} />;
    default:
      return <input type="text" aria-label={def.display_name} defaultValue={value?.text_value || ''} placeholder={def.placeholder || ''} className={cls} onBlur={(e) => onChange(fieldId, { text_value: e.target.value })} />;
  }
}

export const TaskCustomFieldsPanel = observer(({ taskId, editable = true, bare = false }: TaskCustomFieldsPanelProps) => {
  const store = useTask();
  const { t } = useI18n();

  const [values, setValues] = useState<CustomFieldValue[]>([]);
  const [definitions, setDefinitions] = useState<CustomFieldDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res: any = await store.GetTaskCustomFields(taskId);
      setValues(res?.values?.values || []);
      setDefinitions(res?.definitions?.definitions || []);
    } catch {
      // no custom fields configured is not an error surface
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (taskId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  const setField = async (fieldId: string, payload: any) => {
    setError(null);
    try {
      await store.SetTaskCustomField(taskId, fieldId, { field_id: fieldId, value: payload } as any);
      await load();
    } catch (e) {
      setError(errMsg(e, t('task.cfPanel.saveError')));
    }
  };

  const clearField = async (fieldId: string) => {
    setError(null);
    try {
      await store.RemoveTaskCustomField(taskId, fieldId);
      await load();
    } catch (e) {
      setError(errMsg(e, t('task.cfPanel.removeError')));
    }
  };

  if (loading) {
    const skeleton = <div className="h-4 w-24 rounded bg-muted animate-pulse" />;
    if (bare) return skeleton;
    return (
      <Card>
        <CardContent className="p-4">
          {skeleton}
        </CardContent>
      </Card>
    );
  }

  if (definitions.length === 0) return null;

  const sorted = [...definitions].sort((a, b) => (a.position || 0) - (b.position || 0));

  const body = (
    <>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('task.customFields')}</p>
        {editable && (
          <button type="button" onClick={() => setEditMode(!editMode)} className="text-xs text-primary hover:underline">
            {editMode ? t('common.done') : t('common.edit')}
          </button>
        )}
      </div>

      {error && <p className="mb-2 text-xs text-destructive">{error}</p>}

      <div className="space-y-2.5">
        {sorted.map((def) => {
          const fieldId = def.entity?.id || '';
          const value = values.find((v) => v.field_id === fieldId);
          return (
            <div key={fieldId} className="flex items-start justify-between gap-2">
              <span className="text-sm text-muted-foreground shrink-0 pt-0.5">
                {def.display_name}
                {def.is_required && <span className="text-destructive ml-0.5">*</span>}
              </span>
              <div className="text-right min-w-0 flex items-center gap-1.5">
                {editMode ? (
                  <>
                    <FieldInput def={def} value={value} onChange={setField} />
                    {value && (
                      <button
                        type="button"
                        aria-label={t('task.cfPanel.clear')}
                        title={t('task.cfPanel.clear')}
                        onClick={() => clearField(fieldId)}
                        className="text-xs text-destructive hover:underline shrink-0"
                      >
                        ×
                      </button>
                    )}
                  </>
                ) : (
                  renderValue(value, def, t)
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );

  if (bare) {
    return <div>{body}</div>;
  }

  return (
    <Card>
      <CardContent className="p-4">
        {body}
      </CardContent>
    </Card>
  );
});

export default TaskCustomFieldsPanel;
